import React, { useState, useEffect } from "react";
import { Percent } from "lucide-react";
import { useLanguage } from '../context/LanguageContext';
import { useDialogs } from '../context/DialogContext';
import adminApi, { getAdminUsername } from '../adminApi';
import './TaxChange.css';

function TaxChange() {
  const { t, tf } = useLanguage();
  const { confirm } = useDialogs();
  const [currentTax, setCurrentTax] = useState<number>(19.0);
  const [newTaxInput, setNewTaxInput] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Stawka raz, przy wejściu do widoku
  useEffect(() => {
    fetchCurrentTaxRate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCurrentTaxRate = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/tax-rate');
      setCurrentTax(response.data.rate || 19.0);
    } catch (err) {
      console.error('Error fetching tax rate:', err);
      setError(t("Failed to load current tax rate"));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTax = async (e: React.FormEvent) => {
    e.preventDefault();

    setError(null);
    setSuccessMessage(null);

    // Validation
    const newTaxValue = parseFloat(newTaxInput);
    if (isNaN(newTaxValue)) {
      setError(t("Please enter a valid number."));
      return;
    }
    if (newTaxValue < 0 || newTaxValue > 100) {
      setError(t("Tax rate must be between 0 and 100."));
      return;
    }
    if (newTaxValue === currentTax) {
      setError(t("New rate is the same as current rate."));
      return;
    }

    // Confirmation
    const isConfirmed = await confirm({
      message: tf("Are you sure you want to change the tax rate from {from}% to {to}%?", { from: currentTax, to: newTaxValue }),
      confirmLabel: t("Update Tax Rate"),
    });

    if (isConfirmed) {
      setIsUpdating(true);
      try {
        const response = await adminApi.put('/tax-rate', {
          rate: newTaxValue,
          changed_by: getAdminUsername()
        });

        if (response.data.success) {
          setCurrentTax(newTaxValue);
          setSuccessMessage(`${t("Tax rate successfully updated to")} ${newTaxValue}%.`);
          setNewTaxInput("");
          setTimeout(() => setSuccessMessage(null), 5000);
        }
      } catch (err) {
        console.error('Error updating tax rate:', err);
        setError(t("Failed to update tax rate"));
      } finally {
        setIsUpdating(false);
      }
    }
  };

  if (loading) {
    return <div className="admin-empty">{t("Loading tax rate...")}</div>;
  }

  return (
    <>
      <div className="admin-page__header">
        <div>
          <h2>{t("Tax Rate Management")}</h2>
          <p>{t("Manage the global VAT/tax rate applied in the configurator.")}</p>
        </div>
      </div>

      <div className="admin-card tax-card">
        <div className="tax-current">
          <span className="tax-current__label">{t("Current Tax Rate")}</span>
          <strong className="tax-current__value">{currentTax.toFixed(1)}%</strong>
        </div>

        <form className="tax-form" onSubmit={handleUpdateTax}>
          {error && <div className="admin-alert admin-alert--error">{error}</div>}
          {successMessage && <div className="admin-alert admin-alert--success">{successMessage}</div>}

          <div className="admin-field tax-field">
            <label htmlFor="newTaxInput">{t("New Tax Rate (%)")}</label>
            <input
              id="newTaxInput"
              type="number"
              step="0.1"
              min="0"
              max="100"
              placeholder={t("e.g. 23")}
              value={newTaxInput}
              onChange={(e) => setNewTaxInput(e.target.value)}
              disabled={isUpdating}
            />
          </div>

          <button type="submit" className="admin-btn" disabled={isUpdating || !newTaxInput}>
            <Percent size={16} /> {isUpdating ? t("Updating...") : t("Update Tax Rate")}
          </button>
        </form>
      </div>
    </>
  );
}

export default TaxChange;
